import _get from 'lodash/get'
import { z } from 'zod'

import { SupportedMarketKey } from '@nxt-insurance/constants'

import gender from '@flows/configs/common/components/gender'
import location from '@flows/configs/common/steps/location'
import { HOUSEHOLD_STEPS } from '@flows/configs/householdAt/constants'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import * as stepUtils from '@flows/configs/shared/stepUtils'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import { BaseSchema_HouseholdV1GetsafeAt } from '@flows/insurance/schemas'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'

const getErrorMap = (): z.ZodErrorMap => (issue, ctx) => {
  if (issue.path.includes('born_on')) {
    return (
      schemaUtils.bornOnErrorMap(issue, {
        min: 'sharedFlows_householdAt:steps.born_on.errors.tooYoung',
        max: 'sharedFlows_householdAt:steps.born_on.errors.tooOld',
      }) ?? { message: ctx.defaultError }
    )
  }
  return { message: ctx.defaultError }
}

const household_at_personal = (params: StepConfigParams) => {
  const locationStep = location({
    market: SupportedMarketKey.At,
  })

  return {
    schema: z.preprocess(
      (values) => {
        if (stepUtils.hasHouseNumber(values)) {
          return {
            ...values,
            house_number: values.houseNumber,
          }
        }
        return values
      },

      z
        .object({
          born_on: BaseSchema_HouseholdV1GetsafeAt.shape.born_on,
          gender: z.enum(['m', 'f', 'd']),
        })
        .merge(locationStep.schema)
    ),
    errorMap: getErrorMap(),
    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_householdAt:steps.user_data.title',
        },
      },
      {
        type: 'title',
        text: { type: 'translatableText', translationKey: 'sharedFlows_common:steps.gender.title' },
      },
      gender(SupportedMarketKey.At),
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_householdAt:steps.user_data.birthday.title',
        },
      },
      {
        type: 'date',
        name: 'born_on',
        disabledIfValueExists: true,
      },
      ...locationStep.stepContent,
    ],
    subFlow: SubFlow.Common,
    nextStep: HOUSEHOLD_STEPS.household_at_statement_of_fact,
    onSubmit: {
      type: 'simpleHook',
      callback: (flowValues: FlowValues) => {
        return {
          formValues: {
            [params.helperFunctions.getParamPath('born_on')]: _get(flowValues, 'born_on'),
            [params.helperFunctions.getParamPath('zip_code')]: _get(flowValues, 'zip_code'),
            [params.helperFunctions.getParamPath('street')]: _get(flowValues, 'street'),
            [params.helperFunctions.getParamPath('house_number')]: _get(flowValues, 'houseNumber'),
            [params.helperFunctions.getParamPath('town')]: _get(flowValues, 'town'),
          },
        }
      },
    },
  } as const satisfies AnyStepConfiguration
}

export default household_at_personal
